// ============================================================================
// Outils couleur purs : normalisation d'une saisie hexadecimale et mesure de
// lisibilite (contraste WCAG 2.1).
// ----------------------------------------------------------------------------
// Ces fonctions n'ont AUCUNE dependance a React : elles peuvent etre verifiees
// isolement, et reutilisees par tout ecran qui laisse regler une couleur de
// marque. Le composant d'interface vit dans color-field.tsx.
// ============================================================================

// ---------------------------------------------------------------------------
// Outils couleur
// ---------------------------------------------------------------------------

/** Fond sombre réel de l'application (cartes d'univers, listes). */
export const FOND_SOMBRE = '#111318';
/** Fond clair réel des mails et des PDF générés. */
export const FOND_CLAIR = '#FFFFFF';

export type StatutSaisie = 'vide' | 'incomplet' | 'invalide' | 'valide';

export interface LectureSaisie {
  statut: StatutSaisie;
  /** Forme longue normalisée (#RRGGBB), présente seulement si `statut` vaut « valide ». */
  hex: string | null;
  /** Explication affichable, présente sauf quand la saisie est valide. */
  message: string | null;
}

/**
 * Lit une saisie utilisateur et dit précisément où elle en est. On sépare
 * « incomplet » de « invalide » pour ne pas crier à l'erreur pendant la frappe.
 */
export function lireSaisieHex(brut: string): LectureSaisie {
  const sansEspace = (brut ?? '').trim();
  const corps = sansEspace.startsWith('#') ? sansEspace.slice(1) : sansEspace;

  if (corps.length === 0) {
    return { statut: 'vide', hex: null, message: 'Saisissez un code hexadécimal, par exemple #1A2B3C.' };
  }
  if (/[^0-9a-fA-F]/.test(corps)) {
    return {
      statut: 'invalide',
      hex: null,
      message: 'Un code hexadécimal n’accepte que les chiffres 0 à 9 et les lettres A à F.',
    };
  }
  if (corps.length > 6) {
    return {
      statut: 'invalide',
      hex: null,
      message: 'Un code hexadécimal fait 3 ou 6 caractères, par exemple #1A2B3C.',
    };
  }
  if (corps.length === 3) {
    const [r, v, b] = corps;
    return { statut: 'valide', hex: `#${r}${r}${v}${v}${b}${b}`.toUpperCase(), message: null };
  }
  if (corps.length === 6) {
    return { statut: 'valide', hex: `#${corps}`.toUpperCase(), message: null };
  }
  return {
    statut: 'incomplet',
    hex: null,
    message: `Il manque ${(corps.length < 3 ? 3 : 6) - corps.length} caractère${(corps.length < 3 ? 3 : 6) - corps.length > 1 ? 's' : ''} : 3 ou 6 au total.`,
  };
}

/** Décompose un #RRGGBB en canaux 0-255. Suppose une entrée déjà validée. */
function canaux(hex: string): [number, number, number] {
  const c = hex.replace('#', '');
  return [
    parseInt(c.slice(0, 2), 16),
    parseInt(c.slice(2, 4), 16),
    parseInt(c.slice(4, 6), 16),
  ];
}

/** Luminance relative WCAG 2.1. */
function luminance(hex: string): number {
  const lineaire = canaux(hex).map((v) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * lineaire[0] + 0.7152 * lineaire[1] + 0.0722 * lineaire[2];
}

/** Rapport de contraste WCAG entre deux couleurs, de 1 (identique) à 21. */
export function contraste(a: string, b: string): number {
  const la = luminance(a);
  const lb = luminance(b);
  const clair = Math.max(la, lb);
  const sombre = Math.min(la, lb);
  return (clair + 0.05) / (sombre + 0.05);
}

/** Conversion #RRGGBB vers TSL (teinte, saturation, luminosité en pourcentage). */
export function versTsl(hex: string): { t: number; s: number; l: number } {
  const [r255, v255, b255] = canaux(hex);
  const r = r255 / 255, v = v255 / 255, b = b255 / 255;
  const max = Math.max(r, v, b), min = Math.min(r, v, b);
  const l = (max + min) / 2;
  if (max === min) return { t: 0, s: 0, l: l * 100 };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let t: number;
  if (max === r) t = ((v - b) / d + (v < b ? 6 : 0)) / 6;
  else if (max === v) t = ((b - r) / d + 2) / 6;
  else t = ((r - v) / d + 4) / 6;
  return { t: t * 360, s: s * 100, l: l * 100 };
}

/** Conversion TSL vers #RRGGBB. */
export function versHex(t: number, s: number, l: number): string {
  const sn = s / 100, ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((t / 60) % 2) - 1));
  const m = ln - c / 2;
  let r = 0, v = 0, b = 0;
  if (t < 60) [r, v, b] = [c, x, 0];
  else if (t < 120) [r, v, b] = [x, c, 0];
  else if (t < 180) [r, v, b] = [0, c, x];
  else if (t < 240) [r, v, b] = [0, x, c];
  else if (t < 300) [r, v, b] = [x, 0, c];
  else [r, v, b] = [c, 0, x];
  const oct = (n: number) => Math.round((n + m) * 255).toString(16).padStart(2, '0');
  return `#${oct(r)}${oct(v)}${oct(b)}`.toUpperCase();
}

/**
 * Cherche la variante la PLUS PROCHE de la couleur choisie qui atteigne le
 * contraste demandé, en ne touchant qu'à la luminosité. La teinte de la marque
 * est préservée : c'est une proposition, pas une correction imposée.
 * Renvoie null si aucune variante n'y parvient.
 */
function variantePlusLisible(hex: string, fond: string, seuil: number): string | null {
  const { t, s, l } = versTsl(hex);
  // On teste par pas de 2 % en s'éloignant progressivement de la luminosité
  // d'origine, dans les deux sens, et on garde la première qui passe.
  for (let ecart = 2; ecart <= 100; ecart += 2) {
    for (const candidatL of [l + ecart, l - ecart]) {
      if (candidatL < 0 || candidatL > 100) continue;
      const candidat = versHex(t, s, candidatL);
      if (contraste(candidat, fond) >= seuil) return candidat;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Contrôles de lisibilité
// ---------------------------------------------------------------------------

/** Situation réelle dans laquelle la couleur est utilisée. */
export type UsageCouleur =
  /** Texte ou icône de cette couleur sur le fond sombre de l'application. */
  | 'texteSurSombre'
  /** Texte de cette couleur sur le fond blanc des mails et des PDF. */
  | 'texteSurClair'
  /** Aplat de cette couleur avec du texte blanc posé dessus (badges, boutons). */
  | 'fondSousBlanc';

interface Controle {
  usage: UsageCouleur;
  fond: string;
  seuil: number;
  libelle: string;
}

function controles(usages: UsageCouleur[], seuilTexte: number): Controle[] {
  return usages.map((usage) => {
    if (usage === 'texteSurSombre') {
      return {
        usage,
        fond: FOND_SOMBRE,
        seuil: seuilTexte,
        libelle: "sur le fond sombre de l'application (badge d'univers, cartes)",
      };
    }
    if (usage === 'texteSurClair') {
      return {
        usage,
        fond: FOND_CLAIR,
        seuil: seuilTexte,
        libelle: 'sur le fond blanc des mails et des factures',
      };
    }
    return {
      usage,
      fond: FOND_CLAIR,
      seuil: 3,
      libelle: 'en aplat sous du texte blanc (bouton de paiement, en-tête du mail)',
    };
  });
}

export interface AlerteLisibilite {
  libelle: string;
  ratio: number;
  seuil: number;
  suggestion: string | null;
}

/**
 * Seuil plancher WCAG, en-dessous duquel un texte ou un élément d'interface
 * est ILLISIBLE, pas seulement inconfortable. C'est la ligne que la maquette
 * `connexion-espaces.html` trace : « un rapport de contraste inférieur à 3
 * bloque l'enregistrement ». Entre 3 et le seuil demandé (4,5 pour un corps de
 * texte), l'alerte reste affichée mais n'empêche jamais d'enregistrer.
 */
export const SEUIL_BLOQUANT = 3;

/** Vrai quand cette alerte de lisibilité doit BLOQUER l'enregistrement. */
export function estBloquante(alerte: AlerteLisibilite): boolean {
  return alerte.ratio < SEUIL_BLOQUANT;
}

/** Vrai si au moins une alerte de la liste est bloquante. */
export function contientBlocage(alertes: AlerteLisibilite[]): boolean {
  return alertes.some(estBloquante);
}

/**
 * Liste les usages dans lesquels la couleur passe sous le seuil de lisibilité.
 * Exportée pour être testable et réutilisable ailleurs qu'ici.
 */
export function alertesLisibilite(
  hex: string,
  usages: UsageCouleur[],
  seuilTexte: number,
): AlerteLisibilite[] {
  return controles(usages, seuilTexte)
    .map((c) => {
      // Pour un aplat, c'est le BLANC posé dessus qui doit ressortir : on mesure
      // donc la couleur contre le blanc, ce qui revient au même rapport.
      const ratio = contraste(hex, c.usage === 'fondSousBlanc' ? FOND_CLAIR : c.fond);
      if (ratio >= c.seuil) return null;
      return {
        libelle: c.libelle,
        ratio,
        seuil: c.seuil,
        suggestion: variantePlusLisible(hex, c.usage === 'fondSousBlanc' ? FOND_CLAIR : c.fond, c.seuil),
      };
    })
    .filter((a): a is AlerteLisibilite => a !== null);
}

/**
 * Nuances de la couleur choisie : meme teinte, meme saturation, luminosites
 * echelonnees.
 *
 * POURQUOI. Le champ natif <input type="color"> est banni : dans l'application
 * installee sur telephone il ouvre la boite de dialogue du systeme, hors charte
 * et parfois derriere la fenetre. Il fallait donc un autre moyen d'ajuster une
 * couleur AU DOIGT, sans quitter la charte. Ces nuances donnent le reglage fin
 * (plus clair / plus sombre) avec de simples boutons.
 */
export function nuancesDeTeinte(hex: string): string[] {
  const { t, s } = versTsl(hex);
  return [14, 28, 42, 56, 70, 86].map((l) => versHex(t, s, l));
}
